import { get } from 'lodash';
import fetch from 'isomorphic-fetch';
import { clientLogger } from '../../client/lib/logger';

export function updatePath(path) {
  return {
    type: 'UPDATE_MYAPPS_PATH',
    path
  };
}

export function updateApp(key, app) {
  return {
    type: 'UPDATE_MYAPPS_APP',
    key,
    app
  };
}

export function updateTab(key) {
  return {
    type: 'UPDATE_MYAPPS_TAB',
    key
  };
}

function updateTools(tools = {}, favorites = [], activeKey = '0', search) {
  return {
    type: 'UPDATE_MYAPPS_TOOLS',
    tools,
    favorites,
    activeKey,
    search
  };
}

/**
 * Fetch all tools. This is called on the server side
 * when the page is loading.
 * @param {object} app
 */
function getTools(headers = {}) {
  let url = '/merchantapps/api/v1/tools';

  if (headers.merchantHubHost) {
    url = headers.merchantHubHost + url;
  }

  return fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers
  }).then(res => {
    return res.ok ? Promise.resolve(res.json()) : Promise.reject(res.json());
  });
}

/**
 * Update a tool
 * @param {object} tool
 */
function updateTool(csrf, tool = {}) {
  const url = '/merchantapps/api/v1/tools';

  return fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf
    },
    body: JSON.stringify([
      {
        id: tool.id,
        key: tool.key,
        favorite: tool.favorite,
        popularity: tool.popularity
      }
    ])
  }).then(res => {
    return res.ok ? res.json() : Promise.reject(res.json());
  });
}

/**
 * Build tools for displaying logged-out experience.
 * @param {function} dispatch
 * @param {object} state
 */
function buildLoggedOutTools(dispatch, state) {
  const { loggedOutApps } = require('@paypalcorp/node-merchant-apps');
  const { app: { legalCountry } = {} } = state;
  const loggedOutTools = loggedOutApps({ locality: { country: legalCountry } });
  const tools = {};

  loggedOutTools.forEach(e => {
    tools[e] = { key: e };
  });

  dispatch(updateTools(tools, [], '0'));
}

/**
 * Fetching tools for displaying logged-in experience.
 * @param {function} dispatch
 * @param {object} state
 * @param {object} headers
 */
function fetchLoggedInTools(dispatch, state, headers) {
  return getTools(headers)
    .then(data => {
      let activeKey = get(state, 'myapps.activeKey', '0');

      // data.tools is an array
      // make them into an object for faster access
      let tools = {};
      /**
       * TODO this logic need to be modified later on when we want to enable favorites for thirdparty apps.
       * The order of the favorites need to be considered.
       */
      let favorites = [];
      if (Array.isArray(get(data, 'tools', ''))) {
        // if the user is in both the PPCP4B and old nav treatment,
        // we should remove business setup from tools
        if (
          get(state, 'experimentation.ppcp.oldNav') &&
          !get(state, 'experimentation.ppcp.control')
        ) {
          data.tools = data.tools.filter(
            tool => tool.key !== 'pp_business_setup'
          );
        }
        data.tools.forEach(e => {
          tools[e.key] = { type: 'tool', ...e };
          if (e.favorite) {
            favorites.push(e.key);
          }
        });
      }

      dispatch(updateTools(tools, favorites, activeKey));
    })
    .catch(err => {
      clientLogger.warn('error fetching tools', err);
    });
}

/**
 * Fetching tools for displaying.
 * @note At this moment, this function is only called when the page is rendered for the first time.
 * @param {object} params
 * @param {object} query
 * @param {object} headers
 */
export function fetchTools(params, query, headers) {
  return (dispatch, getState) => {
    const state = getState();
    const isLoggedIn = get(state, 'app.context.user.isLoggedIn', false);
    // user object is empty when logged-out
    return !isLoggedIn
      ? buildLoggedOutTools(dispatch, state)
      : fetchLoggedInTools(dispatch, state, headers);
  };
}

export function toggleToolFavorite(tool = {}, search) {
  return (dispatch, getState) => {
    const { app, myapps } = getState();
    const favorites = get(myapps, 'favorites', []),
      tools = get(myapps, 'tools', {}),
      toolKey = get(tool, 'key', ''),
      activeKey = get(myapps, 'activeKey', '0');

    // if toolkey is not correct, there must be some error.
    // we should not proceed to store to database.
    // in the front end, user will still see favorite got toggled
    // but after refresh page, it will be revert back.
    if (!toolKey) {
      // TODO log error messages
      return;
    }

    /**
     * update the store with the changes
     * the page will be re-rendered
     */
    const update = () => {
      let next = {
        ...tools,
        [toolKey]: {
          ...tools[toolKey],
          favorite: typeof tool.favorite === 'boolean' && !tool.favorite
        }
      };

      let nextFavorites = [...favorites];
      if (next[toolKey].favorite) {
        // insert into array
        if (!nextFavorites.includes(toolKey)) {
          // insert into the end of the array
          // TODO we might want to reconsider the order
          nextFavorites.push(toolKey);
        }
      } else if (nextFavorites.includes(toolKey)) {
        // take it out from the array
        nextFavorites.splice(
          nextFavorites.findIndex(e => e === toolKey),
          1
        );
      }

      // update the header `Tools` menu
      let event = new CustomEvent('tools_fav_updated_new', {
        detail: next[toolKey]
      });
      document.dispatchEvent(event);
      dispatch(updateTools(next, nextFavorites, activeKey, search));
    };

    /**
     * refresh the store and re-render with current data
     * so that lower level components' state will be refreshed.
     * this should be called when updateTool database call failed.
     */
    const refresh = () => {
      const { myapps: { tools = {}, favorites = [] } = {} } = getState();
      // need to revert properly
      const next = {
        ...tools,
        [toolKey]: {
          ...tools[toolKey],
          favorite: typeof tool.favorite === 'boolean' && !tool.favorite
        }
      };
      const nextFavorites = [...favorites];
      if (next[toolKey].favorite) {
        // insert into array
        if (!nextFavorites.includes(toolKey)) {
          // insert into the end of the array
          // TODO we might want to reconsider the order
          nextFavorites.push(toolKey);
        }
      } else if (nextFavorites.includes(toolKey)) {
        // take it out from the array
        nextFavorites.splice(
          nextFavorites.findIndex(e => e === toolKey),
          1
        );
      }
      dispatch(updateTools(next, nextFavorites, activeKey, search));
    };

    // update client state before API call for good UX
    update();

    updateTool(get(app, 'csrf', ''), {
      ...tools[toolKey],
      favorite: typeof tool.favorite === 'boolean' && !tool.favorite
    })
      .then(res => {
        // only if the call failed do we roll back the favorite
        // same with the catch
        if (!get(res, 'success', false)) {
          clientLogger.warn('error updating tools', 'unsuccessful response');
          refresh();
        }
      })
      .catch(err => {
        clientLogger.warn('error updating tools', err);
        refresh();
      });
  };
}

export const sendConfirmationEmail = () => {
  return (dispatch, getState) => {
    const {
      app: { csrf = '' }
    } = getState();
    const url = '/merchantapps/api/v1/confirmEmail';

    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrf
      }
    });
  };
};
